import api from '@app/services/api';
import type {
    ApiError,
    DashboardDailyStatsViewParams,
    DashboardGroupStatsViewParams,
    DashboardStatsViewResponse,
} from '@app/services/api.types';
import { apiEndpoints } from '@app/utils/constants';
import type { UseQueryOptions } from '@tanstack/react-query';
import { useQuery } from '@tanstack/react-query';

type QueryOptions = Omit<
    UseQueryOptions<DashboardStatsViewResponse, ApiError>,
    'queryKey' | 'queryFn'
>;

type DailyQueryOptions = Omit<
    UseQueryOptions<DashboardStatsViewResponse, ApiError>,
    'queryKey' | 'queryFn'
>;

export const fetchGroupStats = async (
    params: DashboardGroupStatsViewParams,
    signal?: AbortSignal
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.groupStats;
    const response = await api.get(url, { params, signal });
    return response?.data?.data;
};

export const fetchDailyStats = async (
    params: DashboardDailyStatsViewParams,
    signal?: AbortSignal
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.dailyStats;
    const response = await api.get(url, { params, signal });
    return response?.data?.data;
};

export const fetchGrouped = async (
    params: DashboardDailyStatsViewParams,
    signal?: AbortSignal
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.grouped;
    const response = await api.get(url, { params, signal });
    return response?.data?.data;
};

export const fetchGroupedByBrand = async (
    params: DashboardDailyStatsViewParams,
    signal?: AbortSignal
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.byBrand;
    const response = await api.get(url, { params, signal });
    return response?.data?.data;
};

export const fetchGroupedByLocation = async (
    params: DashboardDailyStatsViewParams,
    signal?: AbortSignal
): Promise<DashboardStatsViewResponse> => {
    const url = apiEndpoints.dashboard.byLocation;
    const response = await api.get(url, { params, signal });
    return response?.data?.data;
};

const useDashboardStats = () => {
    const useGroupStats = (params: DashboardGroupStatsViewParams, options?: QueryOptions) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'stats', params],
            queryFn: ({ signal }) => fetchGroupStats(params, signal),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useDailyStats = (params: DashboardDailyStatsViewParams, options?: DailyQueryOptions) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'daily-stats', params],
            queryFn: ({ signal }) => fetchDailyStats(params, signal),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useGrouped = (params: DashboardDailyStatsViewParams, options?: DailyQueryOptions) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'grouped', params],
            queryFn: ({ signal }) => fetchGrouped(params, signal),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useGroupedByBrand = (
        params: DashboardDailyStatsViewParams,
        options?: DailyQueryOptions
    ) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'by-brand', params],
            queryFn: ({ signal }) => fetchGroupedByBrand(params, signal),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    const useGroupedByLocation = (
        params: DashboardDailyStatsViewParams,
        options?: DailyQueryOptions
    ) => {
        return useQuery<DashboardStatsViewResponse, ApiError>({
            queryKey: ['dashboard', 'by-location', params],
            queryFn: ({ signal }) => fetchGroupedByLocation(params, signal),
            ...options,
            enabled: Boolean(params.start && params.end),
        });
    };

    return {
        useGroupStats,
        useDailyStats,
        useGrouped,
        useGroupedByBrand,
        useGroupedByLocation,
    };
};
export default useDashboardStats;
